<template>
  <div>
    <b-card>
      <div class="d-flex flex-column flex-md-row align-items-md-center justify-content-between mb-2">
        <h2 class="text-blue font-weight-bold mb-2 mb-md-0">
          {{ $t("edit_partner") }}
        </h2>

        <b-button variant="outline-secondary" class="ml-md-auto" @click="goBack">
          {{ $t("back") }}
        </b-button>
      </div>

      <div v-if="loading" class="text-center py-5">
        <b-spinner variant="primary" />
      </div>

      <b-form v-else-if="partnerData" @submit.prevent="submitUpdate">
        <b-alert
            v-if="partnerData.register_link"
            show
            variant="light-success"
            class="mb-3"
        >
          <p class="font-weight-bold mb-1">
            {{ $t('register_link_generated') }}
          </p>
          <b-input-group>
            <b-form-input
                :value="partnerData.register_link"
                readonly
            />
            <b-input-group-append>
              <b-button
                  variant="outline-primary"
                  @click="copyRegistrationLink"
              >
                {{ copyLinkSuccess ? $t('link_copied') : $t('copy_link') }}
              </b-button>
            </b-input-group-append>
          </b-input-group>

          <div class="d-flex flex-column flex-md-row align-items-md-center mt-2">
            <small class="text-muted" v-if="partnerData.invitation_sent_at">
              {{ $t('invitation_last_sent') }}: {{ formatDateTime(partnerData.invitation_sent_at) }}
            </small>
            <b-button
                size="sm"
                variant="success"
                class="mt-1 mt-md-0 ml-md-auto"
                v-if="partnerData.invitation_sent_at"
                :disabled="isSendingInvite"
                @click="sendInvitationEmail"
            >
              <b-spinner small class="mr-50" v-if="isSendingInvite" />
              {{ $t('resend_invitation') }}
            </b-button>
          </div>
        </b-alert>

        <!-- 3 Column Layout -->
        <b-row>
          <!-- Column 1: Company Information -->
          <b-col cols="12" md="4">
            <div class="mb-3">
              <h5 class="mb-2 text-primary">{{ $t('company_information') }}</h5>
              <hr class="mt-0" />
            </div>

            <b-form-group
                :label="$t('company_name')"
                label-for="company-name-input"
            >
              <b-form-input
                  id="company-name-input"
                  v-model.trim="companyForm.company_name"
              />
              <small class="text-danger" v-if="companyFormErrors.company_name">
                {{ companyFormErrors.company_name }}
              </small>
            </b-form-group>

            <b-form-group
                :label="$t('company_email_address')"
                label-for="company-email-input"
            >
              <b-form-input
                  id="company-email-input"
                  type="email"
                  v-model.trim="companyForm.contact_email"
                  required
                  disabled
              />
              <small class="text-muted">{{ $t('email_cannot_be_changed') }}</small>
              <small class="text-danger d-block" v-if="companyFormErrors.contact_email">
                {{ companyFormErrors.contact_email }}
              </small>
            </b-form-group>
          </b-col>

          <!-- Column 2: Partner's Privileges -->
          <b-col cols="12" md="4">
            <div class="mb-3">
              <h5 class="mb-2 text-primary">{{ $t('partner_privileges') }}</h5>
              <hr class="mt-0" />
            </div>

            <b-form-group
                :label="$t('referral_commission')"
                label-for="referral-commission-input"
            >
              <b-input-group append="%">
                <b-form-input
                    id="referral-commission-input"
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    v-model.number="companyForm.referral_commission"
                    required
                />
              </b-input-group>
              <small class="text-danger" v-if="companyFormErrors.referral_commission">
                {{ companyFormErrors.referral_commission }}
              </small>
            </b-form-group>

            <b-form-group
                :label="$t('partner_annual_fee_share_percentage')"
                label-for="partner-fee-share-input"
            >
              <b-input-group append="%">
                <b-form-input
                    id="partner-fee-share-input"
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    v-model.number="companyForm.partner_annual_fee_share_percentage"
                />
              </b-input-group>
              <small class="text-danger" v-if="companyFormErrors.partner_annual_fee_share_percentage">
                {{ companyFormErrors.partner_annual_fee_share_percentage }}
              </small>
            </b-form-group>
          </b-col>

          <!-- Column 3: Member's Privileges -->
          <b-col cols="12" md="4">
            <div class="mb-3">
              <h5 class="mb-2 text-primary">{{ $t('member_privileges') }}</h5>
              <hr class="mt-0" />
            </div>

            <b-form-group
                :label="$t('member_annual_fee_discount')"
                label-for="member-discount-input"
            >
              <b-input-group append="%">
                <b-form-input
                    id="member-discount-input"
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    v-model.number="companyForm.member_annual_fee_discount"
                />
              </b-input-group>
              <small class="text-danger" v-if="companyFormErrors.member_annual_fee_discount">
                {{ companyFormErrors.member_annual_fee_discount }}
              </small>
            </b-form-group>

            <b-form-group
                :label="$t('member_annual_fee_discount_duration')"
                label-for="member-annual-fee-discount-duration-input"
            >
              <b-input-group :append="$t('years')">
                <b-form-input
                    id="member-annual-fee-discount-duration-input"
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    v-model.number="companyForm.member_annual_fee_discount_duration"
                />
              </b-input-group>
              <small class="text-danger" v-if="companyFormErrors.member_annual_fee_discount_duration">
                {{ companyFormErrors.member_annual_fee_discount_duration }}
              </small>
            </b-form-group>

            <b-form-group
                :label="$t('free_period_months')"
                label-for="free-period-input"
            >
              <b-input-group :append="$t('months')">
                <b-form-input
                    id="free-period-input"
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    v-model.number="companyForm.free_period_months"
                />
              </b-input-group>
              <small class="text-danger" v-if="companyFormErrors.free_period_months">
                {{ companyFormErrors.free_period_months }}
              </small>
            </b-form-group>
          </b-col>
        </b-row>

        <small class="text-danger d-block mb-2" v-if="companyFormErrors.member_privileges">
          {{ companyFormErrors.member_privileges }}
        </small>

        <div class="d-flex justify-content-end mt-3">
          <b-button
              variant="outline-secondary"
              class="mr-1"
              @click="goBack"
              :disabled="isSaving"
          >
            {{ $t("cancel") }}
          </b-button>
          <b-button
              variant="primary"
              type="submit"
              :disabled="isSaving"
          >
            <b-spinner small class="mr-50" v-if="isSaving" />
            {{ $t("update") }}
          </b-button>
        </div>
      </b-form>

      <div v-else class="text-center py-5">
        <p class="text-danger">{{ $t('partner_not_found') }}</p>
        <b-button variant="primary" @click="goBack">
          {{ $t("back") }}
        </b-button>
      </div>
    </b-card>
  </div>
</template>

<script>
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  data() {
    return {
      loading: true,
      partnerData: null,
      companyForm: {
        company_name: '',
        contact_email: '',
        referral_commission: 20,
        partner_annual_fee_share_percentage: null,
        member_annual_fee_discount: null,
        member_annual_fee_discount_duration: null,
        free_period_months: null,
      },
      companyFormErrors: {},
      isSaving: false,
      isSendingInvite: false,
      copyLinkSuccess: false,
    };
  },
  created() {
    this.fetchPartnerData();
  },
  methods: {
    async fetchPartnerData() {
      this.loading = true;
      try {
        const partnerId = this.$route.params.id;

        const res = await this.$http.post('/referral-program/company-privileges/list', {
          per_page: 1000
        });

        if (res?.data?.value?.data) {
          this.partnerData = res.data.value.data.find(p => p.id == partnerId);

          if (this.partnerData) {
            this.companyForm = {
              company_name: this.partnerData.company_name || '',
              contact_email: this.partnerData.contact_email || '',
              free_period_months: this.partnerData.free_period_months,
              partner_annual_fee_share_percentage: this.partnerData.partner_annual_fee_share_percentage,
              member_annual_fee_discount: this.partnerData.member_annual_fee_discount,
              member_annual_fee_discount_duration: this.partnerData.member_annual_fee_discount_duration,
              referral_commission: this.partnerData.referral_commission == null ? 20 : this.partnerData.referral_commission,
            };
          }
        }
      } catch (error) {
        console.error('Error fetching partner data:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('error'),
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        this.loading = false;
      }
    },

    validateCompanyForm() {
      const errors = {};

      if (!this.companyForm.contact_email?.trim()) {
        errors.contact_email = this.$t('required');
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.companyForm.contact_email)) {
        errors.contact_email = this.$t('invalid_email');
      }

      if (this.companyForm.referral_commission === null || this.companyForm.referral_commission === '') {
        errors.referral_commission = this.$t('required');
      }

      if (this.companyForm.partner_annual_fee_share_percentage === null || this.companyForm.partner_annual_fee_share_percentage === '') {
        errors.partner_annual_fee_share_percentage = this.$t('required');
      }

      const hasFreePeriod     = this.companyForm.free_period_months !== null && this.companyForm.free_period_months !== '';
      const hasMemberDiscount = this.companyForm.member_annual_fee_discount !== null && this.companyForm.member_annual_fee_discount !== '';

      if (!hasMemberDiscount && !hasFreePeriod) {
        errors.member_privileges = this.$t('member_privileges_hint');
      }

      return errors;
    },

    async submitUpdate() {
      const errors = this.validateCompanyForm();
      this.companyFormErrors = errors;
      if (Object.keys(errors).length) return;

      this.isSaving = true;

      const payload = {
        company_name: this.companyForm.company_name.trim(),
        contact_email: this.companyForm.contact_email.trim(),
        referral_commission: Number(this.companyForm.referral_commission),
        partner_annual_fee_share_percentage: this.companyForm.partner_annual_fee_share_percentage === null || this.companyForm.partner_annual_fee_share_percentage === '' ? null : Number(this.companyForm.partner_annual_fee_share_percentage),
        member_annual_fee_discount: this.companyForm.member_annual_fee_discount === null || this.companyForm.member_annual_fee_discount === '' ? null : Number(this.companyForm.member_annual_fee_discount),
        member_annual_fee_discount_duration: this.companyForm.member_annual_fee_discount_duration === null || this.companyForm.member_annual_fee_discount_duration === '' ? null : Number(this.companyForm.member_annual_fee_discount_duration),
        free_period_months: this.companyForm.free_period_months === null || this.companyForm.free_period_months === '' ? null : Number(this.companyForm.free_period_months),
        send_invitation: false,
      }

      try {
        const res = await this.$http.post('referral-program/company-privileges', payload);

        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('partner_updated_successfully'),
            icon: "CheckIcon",
            variant: "success",
          },
        });

        this.partnerData = res.data.value;

      } catch (error) {
        this.handleUpdateError(error);
      } finally {
        this.isSaving = false;
      }
    },

    handleUpdateError(error) {
      if (error?.response?.data?.errors) {
        const serverErrors = {};
        Object.keys(error.response.data.errors).forEach((key) => {
          [serverErrors[key]] = error.response.data.errors[key];
        });
        this.companyFormErrors = { ...this.companyFormErrors, ...serverErrors };
        return;
      }

      this.$toast({
        component: ToastificationContent,
        props: {
          title: this.$t('error'),
          icon: "AlertTriangleIcon",
          variant: "danger",
        },
      });
    },

    copyRegistrationLink() {
      if (!this.partnerData?.register_link) return;
      const textarea = document.createElement('textarea');
      textarea.value = this.partnerData.register_link;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      this.copyLinkSuccess = true;
      setTimeout(() => {
        this.copyLinkSuccess = false;
      }, 2000);
    },

    async sendInvitationEmail() {
      if (!this.partnerData) return;
      this.isSendingInvite = true;
      try {
        const res = await this.$http.post(`referral-program/company-privileges/${this.partnerData.id}/send-invite`);
        this.partnerData = res.data.value;
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('invitation_sent'),
            icon: "CheckIcon",
            variant: "success",
          },
        });
      } catch (error) {
        const message = error?.response?.data?.message || this.$t('error');
        this.$toast({
          component: ToastificationContent,
          props: {
            title: message,
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        this.isSendingInvite = false;
      }
    },

    formatDateTime(value) {
      if (!value) return '';
      return new Date(value).toLocaleString();
    },

    goBack() {
      this.$router.push({ name: 'partners' });
    },
  },
};
</script>

<style scoped>
</style>
